import * as Sentry from '@sentry/react';

/**
 * Browser-side Sentry initialization for the dashboard.
 *
 * - No-ops silently when `VITE_SENTRY_DSN` is unset. That's the dev default
 *   so local boot doesn't ship events over the network.
 * - When the DSN is present, installs the Sentry React integration +
 *   browser tracing + error sampling. Release + environment ride on
 *   `VITE_SENTRY_RELEASE` / `VITE_SENTRY_ENVIRONMENT`, which CI sets
 *   during deploy (see docs/deployment.md).
 *
 * Call this ONCE at the top of `main.tsx`, before React renders, so the
 * instrumentation is live before any component mounts.
 */
export function initBrowserSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || typeof dsn !== 'string') return false;

  const environment = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE;
  const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
  const tracesSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined,
    import.meta.env.MODE === 'production' ? 0.1 : 1.0,
  );
  const replaysSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string | undefined,
    0, // default off — session replay is a separate cost lever
  );
  const replaysOnErrorSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE as string | undefined,
    0, // default off — enable per-org once the Sentry plan supports replay
  );

  Sentry.init({
    dsn,
    environment,
    release,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend(event) {
      return scrubSensitive(event);
    },
  });
  return true;
}

/**
 * Pure: clamp a string env var to [0, 1]; fall back on unparseable or
 * missing values.
 */
export function parseSampleRate(raw: string | undefined, fallback: number): number {
  if (!raw) return fallback;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  if (parsed > 1) return 1;
  return parsed;
}

const SENSITIVE_KEY_PATTERN = /(password|secret|token|refreshtoken|pin|authorization|cookie|mfacode|otp)/i;

export function scrubSensitive<T>(value: T, seen: WeakSet<object> = new WeakSet()): T {
  if (value === null || typeof value !== 'object') return value;
  if (seen.has(value as object)) return value;
  seen.add(value as object);
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = scrubSensitive(value[i], seen);
    }
    return value;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (SENSITIVE_KEY_PATTERN.test(key)) {
      record[key] = '[REDACTED]';
    } else {
      record[key] = scrubSensitive(record[key], seen);
    }
  }
  return value;
}

/**
 * Re-export Sentry's ErrorBoundary so callers don't need a second
 * import. The dashboard's existing `components/common/ErrorBoundary.tsx`
 * wraps this for consistent HeroUI styling.
 */
export { ErrorBoundary as SentryErrorBoundary } from '@sentry/react';
